import React, { useCallback, useEffect, useState } from "react"

import { Input } from "@coval/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui/select"

import type { AudioTimeRangeFilterConfig } from "@/types/metrics/metadata"

import { AUDIO_TIME_RANGE_MODES } from "../types"

interface TimeRangeFilterEditorProps {
  filter: AudioTimeRangeFilterConfig
  onChange: (filter: AudioTimeRangeFilterConfig) => void
}

type TimeRangeMode = AudioTimeRangeFilterConfig["mode"]

export const TimeRangeFilterEditor: React.FC<TimeRangeFilterEditorProps> = ({
  filter,
  onChange,
}) => {
  const [localCount, setLocalCount] = useState(filter.count.toString())

  useEffect(() => {
    setLocalCount(filter.count.toString())
  }, [filter.count])

  const handleModeChange = useCallback(
    (mode: string) => {
      onChange({ ...filter, mode: mode as TimeRangeMode })
    },
    [filter, onChange],
  )

  const handleCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setLocalCount(value)
    const parsed = parseFloat(value)
    if (!isNaN(parsed) && parsed > 0) {
      onChange({ ...filter, count: parsed })
    }
  }

  const handleCountBlur = () => {
    const parsed = parseFloat(localCount)
    if (isNaN(parsed) || parsed <= 0) {
      setLocalCount("1")
      onChange({ ...filter, count: 1 })
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Select value={filter.mode} onValueChange={handleModeChange}>
        <SelectTrigger className="w-24" aria-label="Time range mode">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {AUDIO_TIME_RANGE_MODES.map((mode) => (
            <SelectItem key={mode.value} value={mode.value}>
              {mode.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Input
        type="number"
        min={0}
        step={1}
        value={localCount}
        onChange={handleCountChange}
        onBlur={handleCountBlur}
        aria-label="Seconds count"
        className="w-20"
      />

      <span className="text-sm text-coval-text-muted">seconds</span>
    </div>
  )
}
